<script setup lang="ts">
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import FilterList from '@/onenetwork/components/filter/FilterList.vue'
import { useRouter } from 'vue-router'
import { computed, onMounted, ref, watch } from 'vue'
import _ from 'lodash'
import { AccountBOActionTypes, useAccountListStore } from '@/modules/account/stores/account.bo.store.ts'
import AccountDetail from '@/modules/account/views/AccountDetail.vue'
import AccountBODetail from '@/modules/account/views/AccountBODetail.vue'
import { useConfirm, useToast } from 'primevue'
import { useReactiveQueryParam } from '@/composables/useReactiveQueryParam'
import { useAccountDetailStore } from '../stores/account.detail.store'
import { useAccountBODetailStore } from '../stores/account.bo.detail.store'


const router = useRouter()
const confirm = useConfirm()
const accountListStore = useAccountListStore()
const accountDetailBOStore = useAccountBODetailStore()
const toast  = useToast()
const pageTabs = computed(() => {
  return accountListStore.tabs
})
const displayTabContent = computed(() => {
  return accountListStore.tabs?.find(tab => tab.active)?.id || 'list'
})
const TableConfig = computed(() => {
  return accountListStore.tableConfig
})
const filter = computed(() => {
  return accountListStore.filter
})
const loading = computed(() => {
  return accountListStore.action_type == AccountBOActionTypes.FETCHING
})
const page = computed(() => {
  return accountListStore.page
})
const pageSize = computed(() => {
  return accountListStore.pageSize
})
const dataSource = computed(() => {
  return accountListStore.data
})

const selectedIds = computed(() => {
  return accountListStore.selectedIds
})


const handleChangeTab = (tab: any) => {

  accountListStore.changeTab(tab.id)
}

const handleCloseTab = (tab: any) => {

  accountListStore.closeTab(tab.id)
}

const handleCloseAllTab = () => {
  accountListStore.closeAllTab()
}

const handleChangePage = (page: number) => {

  accountListStore.changePage(page)
}

const handleChangePageSize = (size: number) => {

  accountListStore.changePageSize(size)
}

const handleUpdateFilter = (filter: any) => {

  accountListStore.setFilter(filter)
}

const handleRowDblClick = (e: MouseEvent, item: any) => {

  accountListStore.newTab({id: item.id, label: `#${item.code} - ${item.full_name}`, active: true})
}

const handleRowClick = (e: MouseEvent, item: any) => {

}

const handleChangeSelectedRow = (ids: Array<string|number>) => {

  accountListStore.setSelectedRow(ids)
}

const handleSort = (field: string, direction: string) => {

  accountListStore.toggleSort(field, direction)
}

const requestFetchAccountList = _.debounce(async () => {
  try{
    const res = await accountListStore.fetchList()
  } catch (e) {

  }
}, 500)

const detailId: any = useReactiveQueryParam('detailId')
const setDetailTab = async () => {
  if(parseInt(detailId.value || 0)){
    try {
      const data: any = await accountDetailBOStore.fetchAccountBO(parseInt(detailId.value))
      accountListStore.newTab({id: data.id, label: `#${data.code} - ${data.full_name}`, active: true})
    } catch (e: any) {
      console.log(e)
    }
  }
}

watch(()=>detailId, (val) => {
  console.log('Giá trị detailId thay đổi:', val.value)
  if(val.value) setDetailTab()
}, {immediate: true})

watch(()=> accountListStore.uuid, (newVal, oldVal) => {
  requestFetchAccountList()
})

onMounted(() => {
  requestFetchAccountList();
})

const handleDeleteSelectedRow = async () => {
  if(selectedIds.value.length === 0) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Vui lòng chọn tài khoản để xóa', life: 3000 });
    return;
  }
  confirm.require({
    message: 'Bạn có muốn xóa tài khoản đã chọn?',
    header: 'Xác nhận',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Đồng ý'
    },
    accept: async () => {
      const res = await accountListStore.deleteSelectedRow()
      if(res) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Xóa tài khoản thành công', life: 3000 });
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Xóa tài khoản thất bại', life: 3000 });
      }
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Error', detail: 'Bạn đã huỷ xóa tài khoản', life: 3000 });
    }
  })
}

const handleResetFilter = () => {
  accountListStore.resetFilter()
}

</script>

<template>
  <dashboard-layout currentRoute="account/bo" page-title="Quản lý tài khoản BackOffice" :tabs="pageTabs" :tabsEvent="{
    close: handleCloseTab,
    select: handleChangeTab,
    closeAll: handleCloseAllTab
  }">
    <template v-if="displayTabContent == 'list'">
      <filter-list
        :filter="filter"
        @update:filter="handleUpdateFilter"
        @reset:filter="handleResetFilter"
      />

      <data-table
        :buttons="[
          {
            label: $t('account.datatable.buttons.add'),
            icon: 'gridicons:create',
            class: 'btn bg-primary bg-hover-primary text-white',
            action: () => {
              router.push({ name: 'account-bo-create' })
            },
            perm: 'account.create_bo',
            selectRequired: false
          },
          {
            label: $t('account.datatable.buttons.delete'),
            icon: 'tabler:trash',
            class: 'btn btn-danger bg-hover-danger',
            action: handleDeleteSelectedRow,
            perm: 'account.delete_bo',
            selectRequired: true
          },
        ]"
        :loading="loading"
        :tableConfig="TableConfig"
        :page-size="pageSize"
        :page="page"
        :total-page="dataSource?.totalPage || 0"
        :total-records="dataSource?.totalRecords || 0"
        :data="dataSource?.list || []"
        :row-action-buttons="[
          { action: handleRowDblClick, css: 'btn btn-cs', label: 'Chi tiết', icon: 'iconamoon:edit-duotone', perm: 'account.view_bo_detail' }
        ]"
        primaryField="code"
        @page-changed="handleChangePage"
        @pagesize-changed="handleChangePageSize"
        @toggle-visibility="accountListStore.toggleColumnVisibility"
        @on-row-click="handleRowClick"
        @on-row-dblclick="handleRowDblClick"
        @on-selected-row="handleChangeSelectedRow"
        @on-sort="handleSort"
      />
    </template>
    <template v-else>
      <template v-for="tab in pageTabs">
        <template v-if="tab.id == displayTabContent">
          <AccountBODetail :id="parseInt(tab.id)" :key="tab.id"/>
        </template>
      </template>
    </template>
  </dashboard-layout>
</template>

<style scoped></style>
